import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Trophy,
  Check,
  Crown,
  ChevronLeft,
  Sparkles,
  ChefHat,
  Droplet,
  Target,
  Lock,
  Share2,
} from "lucide-react";
import { CHALLENGES } from "../data/challenges";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import LockedOverlay from "../components/LockedOverlay";
import { useUpgradeModal } from "../components/AppShell";
import { useShareSheet } from "../components/ShareSheet";
import { toast } from "sonner";

const FILTERS = ["All", "Active", "Skin", "Hair", "Free", "Premium"];

const ICON_BG = {
  "glow-reset-7": "#FDF5E8",
  "hair-growth-21": "#E8FDF0",
  "no-heat-30": "#E8F0FD",
  "hydration-hero-14": "#E8F0FD",
  "dark-spot-10": "#FDE8F0",
};

export default function Challenges() {
  const { profile } = useAuth();
  const upgrade = useUpgradeModal();
  const [enrolled, setEnrolled] = useState({});
  const [active, setActive] = useState(null); // challenge id for detail view

  useEffect(() => {
    if (!profile?.id) return;
    supabase
      .from("user_challenges")
      .select("*")
      .eq("user_id", profile.id)
      .then(({ data }) => {
        const m = {};
        (data || []).forEach((r) => {
          m[r.challenge_id] = r;
        });
        setEnrolled(m);
      });
  }, [profile?.id]);

  const enroll = async (id) => {
    if (!profile?.id) return;
    const { data } = await supabase
      .from("user_challenges")
      .insert({
        user_id: profile.id,
        challenge_id: id,
        start_date: new Date().toISOString().slice(0, 10),
        current_day: 0,
      })
      .select()
      .maybeSingle();
    if (data) {
      setEnrolled((e) => ({ ...e, [id]: data }));
      toast.success("Challenge started — see you tomorrow ✨");
    }
  };

  const logToday = async (id, challenge) => {
    const row = enrolled[id];
    if (!row) return;
    const today = new Date().toISOString().slice(0, 10);
    if (row.last_logged_date === today) return;
    const newDay = Math.min(row.current_day + 1, challenge.duration);
    const completed = newDay >= challenge.duration;
    const { data } = await supabase
      .from("user_challenges")
      .update({ current_day: newDay, last_logged_date: today, completed })
      .eq("id", row.id)
      .select()
      .maybeSingle();
    if (data) {
      setEnrolled((e) => ({ ...e, [id]: data }));
      toast.success(
        completed ? "Challenge complete — badge unlocked!" : `Day ${newDay} done!`
      );
    }
  };

  if (active) {
    const challenge = CHALLENGES.find((c) => c.id === active);
    return (
      <ChallengeDetail
        challenge={challenge}
        enrollment={enrolled[active]}
        onBack={() => setActive(null)}
        onEnroll={() => enroll(active)}
        onLog={() => logToday(active, challenge)}
        isPremium={!!profile?.is_premium}
        onUpgrade={() => upgrade.open(challenge.name)}
      />
    );
  }

  return (
    <ChallengesList
      enrolled={enrolled}
      isPremium={!!profile?.is_premium}
      onOpen={(c) => {
        if (c.premium && !profile?.is_premium) {
          upgrade.open(c.name);
          return;
        }
        setActive(c.id);
      }}
    />
  );
}

function ChallengesList({ enrolled, isPremium, onOpen }) {
  const [filter, setFilter] = useState("All");

  const visible = useMemo(() => {
    return CHALLENGES.filter((c) => {
      if (filter === "All") return true;
      if (filter === "Active") return !!enrolled[c.id] && !enrolled[c.id].completed;
      if (filter === "Free") return !c.premium;
      if (filter === "Premium") return !!c.premium;
      if (filter === "Skin") return c.categories?.includes("Skin");
      if (filter === "Hair") return c.categories?.includes("Hair");
      return true;
    });
  }, [filter, enrolled]);

  return (
    <div className="-mt-px" data-testid="challenges-page">
      {/* PINK GRADIENT HEADER */}
      <div
        className="relative pt-6 px-5 pb-6"
        style={{ background: "linear-gradient(180deg, #C9748A 0%, #B8657A 100%)" }}
        data-testid="challenges-header"
      >
        <h1 className="font-serif text-white text-3xl leading-tight">
          Glow Challenges
        </h1>
        <p className="text-white/85 text-sm font-light mt-0.5">
          Short rituals. Lasting results. Collectible badges.
        </p>
      </div>

      <div className="px-5 pt-4 pb-24">
        {/* Filter pills */}
        <div
          className="flex gap-2 mb-5 overflow-x-auto no-scrollbar -mx-1 px-1"
          data-testid="challenges-filters"
        >
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f.toLowerCase()}`}
              className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                filter === f
                  ? "bg-rose text-white shadow-rose"
                  : "bg-white text-cocoa/60 border border-[#f0e8e4]"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="space-y-4">
          {visible.length === 0 && (
            <p
              className="text-center text-sm text-cocoa/55 font-light py-12"
              data-testid="challenges-empty"
            >
              No challenges match this filter.
            </p>
          )}
          {visible.map((c, i) => (
            <ChallengeCard
              key={c.id}
              c={c}
              enrolled={enrolled[c.id]}
              isPremium={isPremium}
              onOpen={() => onOpen(c)}
              index={i}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function difficultyTone(d) {
  if (d === "Beginner") return { bg: "#E8FDF0", color: "#1A5C35" };
  if (d === "Intermediate") return { bg: "#FDF5E8", color: "#7A4A0A" };
  return { bg: "#FDE8E8", color: "#8B2222" };
}

function ChallengeCard({ c, enrolled, isPremium, onOpen, index }) {
  const progress = enrolled ? (enrolled.current_day / c.duration) * 100 : 0;
  const done = enrolled?.completed;
  const locked = c.premium && !isPremium;
  const diff = difficultyTone(c.difficulty);
  const iconBg = ICON_BG[c.id] || "#FDF8F4";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="bg-white rounded-2xl p-5 border border-[#f0e8e4]"
      data-testid={`challenge-${c.id}`}
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-3">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center text-xl shrink-0"
          style={{ background: iconBg }}
        >
          {done ? <Trophy size={20} className="text-gold" /> : c.emoji}
        </div>
        <div className="flex-1 min-w-0">
          {/* Badge pills row */}
          <div className="flex flex-wrap gap-1.5 mb-1.5">
            <span
              className="text-[10px] font-medium px-2 py-0.5 rounded-full"
              style={{ background: "#FDE8F0", color: "#8B2252" }}
            >
              {c.duration} DAYS
            </span>
            <span
              className="text-[10px] font-medium px-2 py-0.5 rounded-full uppercase"
              style={{ background: diff.bg, color: diff.color }}
            >
              {c.difficulty}
            </span>
            <span
              className="text-[10px] font-medium px-2 py-0.5 rounded-full uppercase"
              style={{
                background: c.premium ? "#F5E8FD" : "#E8F0FD",
                color: c.premium ? "#6B3FA0" : "#1A3D8B",
              }}
            >
              {c.premium ? "Premium" : "Free"}
            </span>
          </div>
          <h3 className="font-serif text-lg leading-tight" style={{ color: "#2E1608" }}>
            {c.name}
          </h3>
          <p className="text-[12px] text-cocoa/55 font-light leading-snug mt-0.5">
            {c.goal}.
          </p>
        </div>
      </div>

      {/* For enrolled: day dots + progress + Continue */}
      {enrolled && !done && (
        <>
          <div className="flex gap-1.5 mb-2 flex-wrap" data-testid={`day-dots-${c.id}`}>
            {Array.from({ length: Math.min(c.duration, 14) }, (_, i) => {
              const dayNum = i + 1;
              const completedDay = dayNum <= enrolled.current_day;
              const isToday = dayNum === enrolled.current_day + 1;
              return (
                <div
                  key={i}
                  className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-medium transition-all"
                  style={
                    completedDay
                      ? { background: "#C9748A", color: "white" }
                      : isToday
                      ? { background: "white", color: "#C9748A", border: "2px solid #C9748A" }
                      : { background: "#f0e8e4", color: "#a89890" }
                  }
                >
                  {dayNum}
                </div>
              );
            })}
            {c.duration > 14 && (
              <span className="text-[10px] text-cocoa/45 self-center ml-1">
                +{c.duration - 14}
              </span>
            )}
          </div>
          <div className="flex justify-between text-[11px] text-cocoa/55 mb-1">
            <span>
              Day {enrolled.current_day} of {c.duration}
            </span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-rose/10 overflow-hidden mb-3">
            <div
              className="h-full transition-all"
              style={{
                width: `${progress}%`,
                background: "linear-gradient(90deg, #C9748A 0%, #C9975A 100%)",
              }}
            />
          </div>
          <button
            onClick={onOpen}
            data-testid={`continue-${c.id}`}
            className="w-full rounded-full py-3 text-sm font-medium text-white shadow-rose"
            style={{ background: "#C9748A" }}
          >
            Continue Today →
          </button>
        </>
      )}

      {/* For completed */}
      {enrolled && done && (
        <button
          onClick={onOpen}
          data-testid={`completed-card-${c.id}`}
          className="w-full rounded-full py-3 text-sm font-medium text-white flex items-center justify-center gap-2"
          style={{ background: "linear-gradient(135deg, #C9975A 0%, #E3BA85 100%)" }}
        >
          <Trophy size={14} /> View achievements
        </button>
      )}

      {/* For unenrolled — free */}
      {!enrolled && !locked && (
        <button
          onClick={onOpen}
          data-testid={`view-${c.id}`}
          className="w-full rounded-full py-3 text-sm font-medium bg-white border-2 border-rose text-rose"
        >
          View Challenge →
        </button>
      )}

      {/* For premium-locked */}
      {!enrolled && locked && (
        <button
          onClick={onOpen}
          data-testid={`unlock-${c.id}`}
          className="w-full rounded-full py-3 text-sm font-medium flex items-center justify-center gap-2"
          style={{ background: "#f0e8e4", color: "#8a7a73" }}
        >
          <Lock size={14} /> Unlock with Premium
        </button>
      )}
    </motion.div>
  );
}

function ChallengeDetail({
  challenge,
  enrollment,
  onBack,
  onEnroll,
  onLog,
  isPremium,
  onUpgrade,
}) {
  const share = useShareSheet();
  const today = new Date().toISOString().slice(0, 10);
  const loggedToday = enrollment?.last_logged_date === today;
  const done = enrollment?.completed;
  const currentDayIdx = enrollment ? enrollment.current_day : 0;
  const todaysDay =
    challenge.days[Math.min(currentDayIdx, challenge.days.length - 1)];
  const progress = enrollment
    ? (enrollment.current_day / challenge.duration) * 100
    : 0;

  const locked = challenge.premium && !isPremium;

  return (
    <div className="px-5 pt-2 pb-10" data-testid={`challenge-detail-${challenge.id}`}>
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={onBack}
          data-testid="challenge-back"
          className="flex items-center gap-1 text-sm text-cocoa/65 font-light"
        >
          <ChevronLeft size={16} /> All challenges
        </button>
        <button
          onClick={() =>
            share.open({
              type: "challenge",
              payload: { title: challenge.name, slug: challenge.id || challenge.slug },
            })
          }
          data-testid="challenge-share-btn"
          className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#f0e8e4] px-3 py-1.5 text-xs font-medium text-rose"
        >
          <Share2 size={12} /> Share
        </button>
      </div>

      {/* Hero card */}
      <div
        className="rounded-[24px] p-6 mb-5 text-white shadow-rose relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #C9748A 0%, #B8657A 100%)" }}
      >
        <div className="flex items-start gap-3 mb-3">
          <div
            className="w-14 h-14 rounded-[18px] bg-white/15 flex items-center justify-center text-2xl backdrop-blur-sm"
          >
            {challenge.emoji}
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-wider opacity-80 mb-0.5">
              {challenge.duration} days · {challenge.difficulty}
            </div>
            <h1 className="font-serif text-3xl leading-tight">
              {challenge.name}
            </h1>
          </div>
        </div>
        <p className="font-serif italic text-base opacity-90 leading-snug">
          &ldquo;{challenge.motivation}&rdquo;
        </p>
        {enrollment && (
          <div className="mt-4">
            <div className="flex justify-between text-xs opacity-85 mb-1">
              <span>
                Day {enrollment.current_day} of {challenge.duration}
              </span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/20 overflow-hidden">
              <div
                className="h-full bg-white"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Locked overlay for premium challenge / non-premium user */}
      {locked ? (
        <div className="relative">
          <LockedOverlay
            title="Unlock this challenge"
            subtitle="Premium challenges include full day-by-day tasks, motivation, and badge rewards."
            onUnlock={onUpgrade}
            testId="challenge-detail-locked"
          />
          <div className="select-none pointer-events-none opacity-40">
            <SectionCard title="What you'll achieve" icon={Sparkles}>
              <ul className="space-y-2">
                {challenge.achievements.map((a) => (
                  <li
                    key={a}
                    className="text-sm text-cocoa font-light flex items-start gap-2"
                  >
                    <span className="w-1.5 h-1.5 mt-2 rounded-full bg-rose shrink-0" />{" "}
                    {a}
                  </li>
                ))}
              </ul>
            </SectionCard>
          </div>
        </div>
      ) : (
        <>
          {/* Goal + achievements */}
          <SectionCard title="The goal" icon={Target}>
            <p className="text-sm text-cocoa font-light leading-relaxed">
              {challenge.goal}
            </p>
          </SectionCard>

          <SectionCard title="What you'll achieve" icon={Sparkles}>
            <ul className="space-y-2">
              {challenge.achievements.map((a) => (
                <li
                  key={a}
                  className="text-sm text-cocoa font-light flex items-start gap-2"
                >
                  <span className="w-1.5 h-1.5 mt-2 rounded-full bg-rose shrink-0" />{" "}
                  {a}
                </li>
              ))}
            </ul>
          </SectionCard>

          {(challenge.drinkDaily || challenge.eatDaily || challenge.avoidFoods) && (
            <SectionCard title="Nutrition guide" icon={ChefHat}>
              {challenge.drinkDaily && (
                <Pill
                  label="Drink daily"
                  items={challenge.drinkDaily}
                  color="rose"
                />
              )}
              {challenge.eatDaily && (
                <Pill
                  label="Eat daily"
                  items={challenge.eatDaily}
                  color="gold"
                />
              )}
              {challenge.avoidFoods && (
                <Pill
                  label="Avoid"
                  items={challenge.avoidFoods}
                  color="cocoa"
                />
              )}
            </SectionCard>
          )}

          {challenge.mustDo && (
            <div
              className="rounded-[20px] p-4 mb-5 flex items-start gap-3"
              style={{ background: "linear-gradient(135deg, #FDF8F4 0%, #F5E6DA 100%)" }}
            >
              <Droplet size={18} className="text-rose mt-0.5 shrink-0" />
              <p className="text-sm text-cocoa font-light leading-relaxed">
                <strong className="font-medium">Must do: </strong>
                {challenge.mustDo}
              </p>
            </div>
          )}

          {/* Today's tasks */}
          {!done && (
            <div className="mb-5" data-testid="todays-tasks">
              <div className="flex items-center justify-between mb-3 px-1">
                <h2 className="font-serif text-2xl text-cocoa">
                  Day {currentDayIdx + 1}
                </h2>
                <span className="text-xs text-cocoa/55">
                  {todaysDay?.title}
                </span>
              </div>
              <div className="bg-white rounded-[20px] p-5 shadow-card space-y-3">
                {(todaysDay?.tasks || []).map((t, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3"
                    data-testid={`task-${i}`}
                  >
                    <span className="w-6 h-6 rounded-full bg-rose/10 text-rose flex items-center justify-center text-[11px] font-medium shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <p className="text-sm text-cocoa font-light leading-relaxed">
                      {t}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="sticky bottom-[88px] pt-2">
            {!enrollment && (
              <button
                onClick={onEnroll}
                data-testid={`enroll-${challenge.id}`}
                className="w-full rounded-full bg-rose hover:bg-rose-dark text-white py-3.5 text-sm font-medium shadow-rose"
              >
                Start this challenge
              </button>
            )}
            {enrollment && !done && (
              <button
                onClick={onLog}
                disabled={loggedToday}
                data-testid={`log-${challenge.id}`}
                className="w-full rounded-full bg-rose hover:bg-rose-dark text-white py-3.5 text-sm font-medium shadow-rose disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loggedToday ? (
                  <>
                    <Check size={16} /> Day {enrollment.current_day} completed
                  </>
                ) : (
                  <>Mark today complete</>
                )}
              </button>
            )}
            {done && (
              <div
                className="w-full rounded-full py-3.5 text-sm font-medium text-white flex items-center justify-center gap-2"
                style={{
                  background:
                    "linear-gradient(135deg, #C9975A 0%, #E3BA85 100%)",
                }}
                data-testid={`completed-${challenge.id}`}
              >
                <Trophy size={16} /> Challenge complete
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SectionCard({ title, icon: Icon, children }) {
  return (
    <div className="bg-white rounded-[20px] p-5 shadow-card mb-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={14} className="text-gold" />
        <p className="text-[10px] uppercase tracking-[0.18em] text-gold font-medium">
          {title}
        </p>
      </div>
      {children}
    </div>
  );
}

function Pill({ label, items, color }) {
  const colorMap = {
    rose: "bg-rose/10 text-rose",
    gold: "bg-gold/15 text-gold",
    cocoa: "bg-cocoa/10 text-cocoa",
  };
  return (
    <div className="mb-3 last:mb-0">
      <p className="text-[10px] uppercase tracking-wider text-cocoa/55 mb-1.5">
        {label}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it) => (
          <span
            key={it}
            className={`text-[11px] px-2.5 py-1 rounded-full font-light ${colorMap[color]}`}
          >
            {it}
          </span>
        ))}
      </div>
    </div>
  );
}
